/**
 * Entrée de l'application : thème (tokens legacy), shell deux modes,
 * dashboard branché sur le poller live.json (specs/01 + 04).
 */
import "./style.css";
import { installTheme } from "./app/theme.ts";
import { LiveClient } from "./app/live.ts";
import { mountShell, modeFromHash } from "./ui/shell.ts";
import { Dashboard } from "./ui/dashboard.ts";
import { Simulation } from "./ui/simulation.ts";
import { Projection } from "./ui/projection.ts";
import { StatsPage } from "./ui/statsPage.ts";
import { renderMethodology } from "./ui/methodology.ts";
import { registerTeamTooltips } from "./ui/teamTooltip.ts";
import { registerMatchPanel } from "./ui/matchPanel.ts";
import { registerStatGraph } from "./ui/statGraphTip.ts";
import { registerPlayerStats } from "./ui/playerStatsTip.ts";
import { registerPlayerProfile } from "./ui/playerProfile.ts";
import { registerCountryProfile } from "./ui/countryProfile.ts";
import { registerSquadValue } from "./ui/squadValue.ts";
import { registerPlayerRatings } from "./ui/playerRatings.ts";
import { preloadTeamRatings } from "./app/teamRatings.ts";
import { preloadInjuries } from "./app/injuryData.ts";
import { track, initTracking } from "./app/track.ts";
import { initLang, lang } from "./app/i18n.ts";

installTheme();
preloadTeamRatings(); // index des notes d'équipe par match (pour les pastilles autour du score)
preloadInjuries(); // absences (blessés/suspendus) : badge fiche pays + mini-terrain + modèle
// Langue (FR/EN/ES) déterminée AVANT tout rendu : choix mémorisé > navigateur >
// pays (GeoIP, /api/geo) > anglais. Bloquant uniquement dans le cas rare où il
// faut interroger le pays (navigateur non FR/EN/ES) — sinon résolu en synchrone.
await initLang();
document.documentElement.lang = lang();

const root = document.getElementById("app");
if (!root) throw new Error("#app introuvable");

const shell = mountShell(root);
renderMethodology(shell.method); // page « Méthode » : contenu statique, rendu une fois

const poller = new LiveClient();
const dashboard = new Dashboard(shell.dashboard, { streamingProbe: () => poller.streaming() });
// Mode Simulation : déroulé d'un tournoi prédit ; « depuis le réel » repart des
// résultats déjà joués du dashboard.
const simulation = new Simulation(shell.sim, { getReal: () => dashboard.snapshotForSimulation() });
// Mode Projection : choisir une équipe → parcours probable + adversaires possibles,
// avec what-if (fixer des résultats de groupe → Monte Carlo conditionné).
const projection = new Projection(shell.projection, { getBase: () => dashboard.projectionBase() });
// Page Statistiques : totaux + tables triables (équipes/joueurs/matchs), temps réel.
const statsPage = new StatsPage(shell.stats, { getState: () => dashboard.getOfficial() });
// Tooltip de parcours au survol de N'IMPORTE quel drapeau : état réel sous le
// dashboard, état du scénario sous la simulation.
registerTeamTooltips(shell.dashboard, () => dashboard.getOfficial());
registerTeamTooltips(shell.sim, () => simulation.getOfficial());
registerTeamTooltips(shell.projection, () => dashboard.getOfficial());
// Panneau « plateau TV » au survol d'une carte de match (bande Aujourd'hui).
registerMatchPanel(shell.dashboard, () => dashboard.getOfficial());
// Graphe d'évolution au survol d'une stat (panneau de match + carte live).
registerStatGraph(() => dashboard.getOfficial());
// Fiche joueur au survol d'une pastille du mini-terrain (compositions).
registerPlayerStats();
// Clic/tap sur un joueur (classements, équipe type, mini-terrain) → fiche dédiée.
registerPlayerProfile();
// Clic/tap sur un drapeau (n'importe où) → fiche pays riche (sélection + résultats).
registerCountryProfile(() => dashboard.getOfficial());
// Clic sur la VALEUR d'une équipe (classement Elo) → modale détail joueur + source.
registerSquadValue();
// Clic sur la NOTE d'une équipe (classement Elo) → modale notes joueurs (moyenne CdM).
registerPlayerRatings();
// La simulation démarre à la PREMIÈRE ouverture de l'onglet (pas au chargement)
// et se resynchronise sur les résultats réels à chaque réouverture.
shell.onModeChange((mode) => {
  if (mode === "sim") simulation.activate();
  else if (mode === "projection") projection.activate();
  else if (mode === "stats") statsPage.activate();
});
// Page Statistiques : survol drapeau/match → tooltips ; clic équipe/joueur/match →
// fiches (les fiches pays/joueur sont globales ; le panneau match est par racine).
registerTeamTooltips(shell.stats, () => dashboard.getOfficial());
registerMatchPanel(shell.stats, () => dashboard.getOfficial());
// Audience first-party (cookieless) : vue de page + chaque mode visité (le 1er est
// émis par le setMode initial ci-dessous). Les ouvertures de fiches sont trackées
// dans matchPanel/playerProfile/countryProfile.
shell.onModeChange((mode) => track("mode", { mode }));
initTracking();
shell.setMode(modeFromHash());
window.addEventListener("hashchange", () => shell.setMode(modeFromHash()));

poller.onUpdate((update) => {
  shell.renderFreshness(update, Date.now());
  dashboard.onLiveUpdate(update);
  // La projection lit l'état officiel ; sans ça, recharger sur l'onglet Projection
  // la fige sur l'état VIDE initial (modèle pur ~80 %) au lieu des vrais résultats.
  projection.onBaseUpdated();
  statsPage.onUpdate(); // tables équipes/matchs en temps réel (si l'onglet est ouvert)
});
// Probabilités calculées par le serveur (SSE) — sinon le worker du navigateur.
poller.onDerived((derived) => dashboard.onServerProbs(derived));
shell.onRefreshClick(() => poller.refresh());
shell.onReplayClick(() => dashboard.replayNow());
poller.start();

// fraîcheur re-rendue toutes les 10 s (« mis à jour il y a 42 s »)
setInterval(() => shell.renderFreshness(poller.current, Date.now()), 10_000);
